import * as React from 'react';
import { styled } from '@linaria/react';

import { random } from '@/utils';

function Click({
  frameNum,
  totalNumOfFrames,
}: {
  frameNum: number;
  totalNumOfFrames: number;
}) {
  return (
    <Wrapper
      key={frameNum}
      style={{
        left: `${(frameNum / totalNumOfFrames) * 100}%`,
      }}
    >
      <Bar />
      <TextWrapper>
        <ClickTextSvg />

        {/* Left */}
        <Particle
          top={-6}
          left={-2}
          angle={220}
          distance={12}
          duration={400}
          delay={16.666 * 3}
        />
        <Particle
          top={2}
          left={-10}
          angle={170}
          distance={7}
          duration={290}
          delay={0}
        />

        {/* Right */}
        <Particle
          top={-6}
          left={30}
          angle={-40}
          distance={8}
          duration={440}
          delay={16.666 * 5}
        />
        <Particle
          top={0}
          left={36}
          angle={0}
          distance={5}
          duration={240}
          delay={0}
        />
      </TextWrapper>
    </Wrapper>
  );
}

const Particle = React.memo(function Particle({
  top,
  left,
  angle,
  distance,
  duration,
  delay,
}: {
  top: number;
  left: number;
  angle: number;
  distance: number;
  delay: number;
  duration: number;
}) {
  const [isFiring, setIsFiring] = React.useState(delay === 0);

  React.useEffect(() => {
    window.setTimeout(() => {
      setIsFiring(true);
    }, delay);
  }, [delay]);

  if (!isFiring) {
    return null;
  }

  return (
    <StyledParticle
      style={{
        top,
        left,
        '--angle': `${random(angle - 10, angle + 10)}deg`,
        '--distance': `${distance}px`,
        '--duration': `${duration}ms`,
      }}
    />
  );
});

function ClickTextSvg(props: React.HTMLAttributes<SVGElement>) {
  return (
    <Svg
      viewBox="0 0 55 23"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M6.8125 7.10938C7.35417 7.10938 7.88021 7.1875 8.39062 7.34375C8.90104 7.5 9.34896 7.71354 9.73438 7.98438C10.1302 8.24479 10.4479 8.54688 10.6875 8.89062C10.9271 9.22396 11.0469 9.56771 11.0469 9.92188C11.0469 10.151 11 10.375 10.9062 10.5938C10.8125 10.8021 10.6875 10.9896 10.5312 11.1562C10.375 11.3125 10.1979 11.4427 10 11.5469C9.80208 11.6406 9.59375 11.6875 9.375 11.6875C9.13542 11.6875 8.91667 11.599 8.71875 11.4219C8.52083 11.2344 8.30729 11.0312 8.07812 10.8125C7.85938 10.5833 7.60938 10.3802 7.32812 10.2031C7.05729 10.0156 6.72917 9.92188 6.34375 9.92188C5.96875 9.92188 5.63021 10.0104 5.32812 10.1875C5.02604 10.3646 4.77083 10.6146 4.5625 10.9375C4.36458 11.25 4.20833 11.625 4.09375 12.0625C3.98958 12.4896 3.9375 12.9583 3.9375 13.4688C3.9375 14.9271 4.21354 16.0417 4.76563 16.8125C5.31771 17.5729 6.10938 17.9531 7.14063 17.9531C7.63021 17.9531 8.04688 17.875 8.39062 17.7188C8.74479 17.5625 9.05208 17.3906 9.3125 17.2031C9.58333 17.0156 9.82812 16.8438 10.0469 16.6875C10.2656 16.5312 10.4896 16.4531 10.7188 16.4531C10.8438 16.4531 10.9688 16.4948 11.0938 16.5781C11.2188 16.651 11.3281 16.75 11.4219 16.875C11.526 16.9896 11.6094 17.1198 11.6719 17.2656C11.7448 17.4115 11.7812 17.5573 11.7812 17.7031C11.7812 18.0469 11.6094 18.401 11.2656 18.7656C10.9323 19.1302 10.5 19.4635 9.96875 19.7656C9.4375 20.0573 8.84375 20.2969 8.1875 20.4844C7.54167 20.6719 6.91146 20.7656 6.29688 20.7656C5.47396 20.7656 4.71875 20.6042 4.03125 20.2812C3.34375 19.9688 2.75 19.5312 2.25 18.9688C1.75 18.3958 1.35938 17.7135 1.07813 16.9219C0.807292 16.1302 0.671875 15.2552 0.671875 14.2969C0.671875 13.2552 0.822917 12.2969 1.125 11.4219C1.4375 10.5469 1.86458 9.79167 2.40625 9.15625C2.95833 8.51042 3.60938 8.01042 4.35938 7.65625C5.10938 7.29167 5.92708 7.10938 6.8125 7.10938ZM12.6081 2.20312C12.6081 1.66146 12.7644 1.27604 13.0769 1.04688C13.3894 0.817708 13.905 0.703125 14.6237 0.703125C14.8737 0.703125 15.0925 0.729167 15.28 0.78125C15.4675 0.833333 15.6237 0.921875 15.7487 1.04688C15.8737 1.16146 15.9727 1.32292 16.0456 1.53125C16.129 1.72917 16.1967 1.98437 16.2487 2.29687C16.3842 3.22396 16.5196 4.19792 16.655 5.21875C16.8008 6.22917 16.9362 7.23958 17.0612 8.25C17.1967 9.25 17.3217 10.2292 17.4362 11.1875C17.5508 12.1354 17.6498 13.0208 17.7331 13.8438C17.8269 14.6562 17.8946 15.3802 17.9362 16.0156C17.9883 16.6406 18.0144 17.1354 18.0144 17.5C18.0144 17.875 17.9779 18.1927 17.905 18.4531C17.8425 18.7135 17.7279 18.9219 17.5612 19.0781C17.405 19.2344 17.1967 19.349 16.9362 19.4219C16.6862 19.4844 16.3737 19.5156 15.9987 19.5156C15.6342 19.5156 15.3425 19.4219 15.1237 19.2344C14.9154 19.0469 14.8112 18.7604 14.8112 18.375C14.8112 17.9792 14.78 17.4531 14.7175 16.7969C14.655 16.1302 14.5717 15.3906 14.4675 14.5781C14.3633 13.7656 14.2435 12.901 14.1081 11.9844C13.9831 11.0677 13.8477 10.1615 13.7019 9.26562C13.5665 8.36979 13.431 7.51042 13.2956 6.6875C13.1706 5.85417 13.056 5.11979 12.9519 4.48438C12.8477 3.83854 12.7644 3.3125 12.7019 2.90625C12.6394 2.48958 12.6081 2.25521 12.6081 2.20312ZM23.1781 4.0625C22.876 4.0625 22.5896 4.02083 22.3188 3.9375C22.0583 3.84375 21.8292 3.71875 21.6313 3.5625C21.4438 3.39583 21.2927 3.20312 21.1781 2.98437C21.0635 2.76562 21.0063 2.52604 21.0063 2.26562C21.0063 1.82812 21.1781 1.47396 21.5219 1.20312C21.876 0.932292 22.3448 0.796875 22.9281 0.796875C23.199 0.796875 23.4594 0.848958 23.7094 0.953125C23.9594 1.04687 24.1781 1.17708 24.3656 1.34375C24.5635 1.51042 24.7198 1.70833 24.8344 1.9375C24.949 2.15625 25.0063 2.38542 25.0063 2.625C25.0063 3.07292 24.8396 3.42708 24.5063 3.6875C24.1833 3.9375 23.7406 4.0625 23.1781 4.0625ZM21.0375 8.82812C21.0375 8.46354 21.1938 8.16667 21.5063 7.9375C21.8292 7.69792 22.251 7.57812 22.7719 7.57812C23.7094 7.57812 24.2094 7.90104 24.2719 8.54688C24.3135 8.99479 24.3552 9.5 24.3969 10.0625C24.449 10.625 24.4958 11.224 24.5375 11.8594C24.5896 12.4844 24.6313 13.1354 24.6625 13.8125C24.7042 14.4792 24.7406 15.1406 24.7719 15.7969C24.8031 16.4427 24.8292 17.0677 24.85 17.6719C24.8708 18.276 24.8813 18.8281 24.8813 19.3281C24.8813 19.849 24.6938 20.2396 24.3188 20.5C23.9542 20.7604 23.4177 20.8906 22.7094 20.8906C22.3865 20.8906 22.1313 20.8125 21.9438 20.6562C21.7667 20.5104 21.6781 20.2917 21.6781 20C21.6781 18.5625 21.6417 17.2188 21.5688 15.9688C21.5063 14.7083 21.4333 13.5885 21.35 12.6094C21.2771 11.6302 21.2042 10.8177 21.1313 10.1719C21.0688 9.51562 21.0375 9.06771 21.0375 8.82812ZM33.6075 7.10937C34.1492 7.10937 34.6752 7.1875 35.1856 7.34375C35.696 7.5 36.144 7.71354 36.5294 7.98437C36.9252 8.24479 37.2429 8.54687 37.4825 8.89062C37.7221 9.22396 37.8419 9.56771 37.8419 9.92187C37.8419 10.151 37.795 10.375 37.7013 10.5937C37.6075 10.8021 37.4825 10.9896 37.3263 11.1562C37.17 11.3125 36.9929 11.4427 36.795 11.5469C36.5971 11.6406 36.3888 11.6875 36.17 11.6875C35.9304 11.6875 35.7117 11.599 35.5138 11.4219C35.3158 11.2344 35.1023 11.0312 34.8731 10.8125C34.6544 10.5833 34.4044 10.3802 34.1231 10.2031C33.8523 10.0156 33.5242 9.92187 33.1388 9.92187C32.7638 9.92187 32.4252 10.0104 32.1231 10.1875C31.821 10.3646 31.5658 10.6146 31.3575 10.9375C31.1596 11.25 31.0033 11.625 30.8888 12.0625C30.7846 12.4896 30.7325 12.9583 30.7325 13.4688C30.7325 14.9271 31.0085 16.0417 31.5606 16.8125C32.1127 17.5729 32.9044 17.9531 33.9356 17.9531C34.4252 17.9531 34.8419 17.875 35.1856 17.7188C35.5398 17.5625 35.8471 17.3906 36.1075 17.2031C36.3783 17.0156 36.6231 16.8438 36.8419 16.6875C37.0606 16.5312 37.2846 16.4531 37.5138 16.4531C37.6388 16.4531 37.7638 16.4948 37.8888 16.5781C38.0138 16.651 38.1231 16.75 38.2169 16.875C38.321 16.9896 38.4044 17.1198 38.4669 17.2656C38.5398 17.4115 38.5763 17.5573 38.5763 17.7031C38.5763 18.0469 38.4044 18.401 38.0606 18.7656C37.7273 19.1302 37.295 19.4635 36.7638 19.7656C36.2325 20.0573 35.6388 20.2969 34.9825 20.4844C34.3367 20.6719 33.7065 20.7656 33.0919 20.7656C32.269 20.7656 31.5138 20.6042 30.8263 20.2812C30.1388 19.9688 29.545 19.5312 29.045 18.9688C28.545 18.3958 28.1544 17.7135 27.8731 16.9219C27.6023 16.1302 27.4669 15.2552 27.4669 14.2969C27.4669 13.2552 27.6179 12.2969 27.92 11.4219C28.2325 10.5469 28.6596 9.79167 29.2013 9.15625C29.7533 8.51042 30.4044 8.01042 31.1544 7.65625C31.9044 7.29167 32.7221 7.10937 33.6075 7.10937ZM40.2244 19.6875C40.2244 18.7812 40.1983 17.7969 40.1463 16.7344C40.0942 15.6719 40.0265 14.599 39.9431 13.5156C39.8702 12.4219 39.7869 11.3542 39.6931 10.3125C39.6098 9.27083 39.5265 8.31771 39.4431 7.45312C39.3702 6.58854 39.3077 5.84375 39.2556 5.21875C39.2035 4.59375 39.1775 4.15625 39.1775 3.90625C39.1775 3.48958 39.3338 3.17187 39.6463 2.95312C39.9588 2.73437 40.4119 2.625 41.0056 2.625C41.266 2.625 41.4848 2.65104 41.6619 2.70312C41.8494 2.75521 42.0004 2.84375 42.115 2.96875C42.2296 3.09375 42.3181 3.26042 42.3806 3.46875C42.4535 3.66667 42.5056 3.91667 42.5369 4.21875C42.5785 4.63542 42.615 5.08333 42.6463 5.5625C42.6775 6.03125 42.7088 6.56771 42.74 7.17187C42.7817 7.77604 42.8233 8.45833 42.865 9.21875C42.9067 9.97917 42.9588 10.8542 43.0213 11.8438C43.0213 11.9896 43.0473 12.0938 43.0994 12.1562C43.1515 12.2188 43.2088 12.25 43.2713 12.25C43.3858 12.25 43.49 12.1562 43.5838 11.9688C44.4692 10.1667 45.3963 8.79167 46.365 7.84375C47.3338 6.88542 48.2765 6.40625 49.1931 6.40625C49.7348 6.40625 50.1619 6.56771 50.4744 6.89062C50.7869 7.21354 50.9431 7.64583 50.9431 8.1875C50.9431 8.77083 50.8129 9.3125 50.5525 9.8125C50.3025 10.3021 49.9848 10.7656 49.5994 11.2031C49.2244 11.6406 48.8129 12.0521 48.365 12.4375C47.9275 12.8229 47.516 13.1979 47.1306 13.5625C46.7556 13.9271 46.4379 14.2865 46.1775 14.6406C45.9275 14.9844 45.8025 15.3333 45.8025 15.6875C45.8025 16.0104 45.99 16.3438 46.365 16.6875C46.7504 17.0208 47.3025 17.3542 48.0213 17.6875C48.74 18.0104 49.6202 18.3281 50.6619 18.6406C51.7035 18.9427 52.8858 19.2344 54.2088 19.5156C54.4171 19.5677 54.5629 19.651 54.6463 19.7656C54.74 19.8802 54.7869 20.0312 54.7869 20.2188C54.7869 20.4479 54.7348 20.6823 54.6306 20.9219C54.5265 21.1719 54.3963 21.3958 54.24 21.5938C54.0838 21.8021 53.9067 21.9688 53.7088 22.0938C53.5213 22.2292 53.339 22.2969 53.1619 22.2969C52.8077 22.2969 52.3858 22.25 51.8963 22.1562C51.4067 22.0625 50.8754 21.9323 50.3025 21.7656C49.74 21.6094 49.1567 21.4219 48.5525 21.2031C47.9483 20.9948 47.3598 20.7656 46.7869 20.5156C46.2244 20.276 45.6879 20.026 45.1775 19.7656C44.6775 19.4948 44.2452 19.2292 43.8806 18.9688C43.766 18.9062 43.6723 18.8542 43.5994 18.8125C43.5369 18.7708 43.4796 18.75 43.4275 18.75C43.365 18.75 43.3285 18.776 43.3181 18.8281C43.3077 18.8698 43.3025 18.9635 43.3025 19.1094C43.3025 19.474 43.2713 19.7708 43.2088 20C43.1567 20.2292 43.0629 20.4062 42.9275 20.5312C42.7921 20.6667 42.6046 20.7604 42.365 20.8125C42.1254 20.8646 41.8285 20.8906 41.4744 20.8906C41.0056 20.8906 40.6775 20.8021 40.49 20.625C40.3129 20.4479 40.2244 20.1354 40.2244 19.6875Z"
        fill="currentColor"
      />
    </Svg>
  );
}

const Wrapper = styled.div`
  position: absolute;
  top: -4px;
  bottom: 0;
  color: white;
`;

const Bar = styled.div`
  position: absolute;
  inset: 0;
  width: 2px;
  height: 100%;
  background: currentColor;
  border-radius: 2px 2px 0 0;
  transform: translateX(1px);
`;

const TextWrapper = styled.div`
  position: absolute;
  top: -4px;
  width: 27.5px;
  transform: translate(-50%, -100%);
  transform-origin: bottom center;
  animation: clickTextEnter 400ms cubic-bezier(0.23, 0.97, 0.44, 1.24);

  @keyframes clickTextEnter {
    from {
      transform: translate(-50%, -50%) scale(0.5);
    }
    to {
      transform: translate(-50%, -100%);
    }
  }
`;
const Svg = styled.svg`
  display: block;
  width: 100%;
  max-width: revert;
`;

const StyledParticle = styled.div`
  --fade-curve: cubic-bezier(1, -0.01, 0.83, 1.15);
  position: absolute;
  width: 6px;
  height: 3px;
  border-radius: 99px;
  background: currentColor;
  /* prettier-ignore */
  animation:
    particleZoom var(--duration) both linear,
    particleFade var(--duration) both var(--fade-curve);

  @keyframes particleZoom {
    from {
      transform: rotate(var(--angle)) translateX(0);
    }
    to {
      transform: rotate(var(--angle)) translateX(var(--distance));
    }
  }
  @keyframes particleFade {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
`;

export default Click;
